<template>
  <div class="page_content detail-view-section">

    <semester-header :stream-uuid="streamUuid"
                     :current-semester="currentSemester"
                     :current-year="currentYear"
                     :parent-component="'Detail'"></semester-header>

  	<div class="detail-view-table">
  		<b-table hover :items="formattedCourses" :fields='formattedFields'>
  		  <template slot="remove" slot-scope="data">
  			<b-button @click.stop='removeButtonClicked(data.item)' aria-label="remove">X</b-button>
  		  </template>
  		</b-table>
  	</div>

  </div>
</template>

<script>
  import '../../../assets/module/css/semester/calendarView.min.css'
  import { mapActions, mapState } from 'vuex'
  import SemesterHeader from '../../semester/components/SemesterHeader'
  import { PACKAGE } from '../../../package_constants'
  import { PackageUtil } from '../../../package_utils'

  export default {
    name: 'semester-detail-view',
    props: ['streamUuid', 'currentSemester', 'currentYear'],
    components: {
      SemesterHeader
    },
    data: () => {
      return {
        fields: [],
        userRole: localStorage.getItem('userrole')
      }
    },
    mounted () {
      this.loadData()
    },
    methods: {
      ...mapActions([
        'populateCourses',
        'removeCourseFromStream',
        'getICalFile'
      ]),
      loadData () {
        let streamUuid = this.streamUuid
        let currentSemester = this.currentSemester
        let currentYear = this.currentYear

        if (!streamUuid || !currentSemester || !currentYear) {
          return this.$router.push({ name: 'courseSearchResults' })
        }

        this.populateCourses({streamUuid: streamUuid, currentYear: currentYear, currentSemester: currentSemester})
      },
      notifySuccess (text) {
        this.$notify({
          group: 'notify',
          type: 'success',
          title: '',
          text: text
        })
      },
      notifyFailure (text) {
        this.$notify({
          group: 'notify',
          type: 'error',
          title: '',
          text: text
        })
      },
      onShown () {
        console.log('onShown called')
      },
      writeFile (data) {
        const blob = new Blob([data], {type: 'text/plain'})
        const e = document.createEvent('MouseEvents')
        let a = document.createElement('a')
        a.download = 'my_download.ics'
        a.href = window.URL.createObjectURL(blob)
        a.dataset.downloadurl = ['text/json', a.download, a.href].join(':')
        e.initEvent('click', true, false, window, 0, 0, 0, 0, 0, false, false, false, false, 0, null)
        a.dispatchEvent(e)
      },
      exportAsICal () {
        let streamUuid = this.streamUuid
        let currentSemester = this.currentSemester
        let currentYear = this.currentYear

        this.getICalFile({streamUuid: streamUuid, currentYear: currentYear, currentSemester: currentSemester})
        let iCalFile = this.iCalEvents
        this.writeFile(iCalFile)
      },
      shareButtonClicked () {
        console.log('shareButtonClicked clicked!')
      },
      goToPlanning () {
        console.log('goToPlanning clicked!')
      },
      goToCalendarView () {
        let streamUuid = this.streamUuid
        let currentSemester = this.currentSemester
        let currentYear = this.currentYear
        return this.$router.push({ name: 'semesterCalendarView', params: {streamUuid: streamUuid, currentYear: currentYear, currentSemester: currentSemester} })
      },
      printButtonClicked () {
        window.print()
      },
      formatInstructorNames (instructorNames) {
        let names = ''
        let max = 8
        let len = instructorNames.length
        for (let instructorNameIndex in instructorNames) {
          let instructorName = instructorNames[instructorNameIndex]
          if (instructorNameIndex === max) {
            names += ' ...'
            break
          } else if (instructorNameIndex === len - 1 && len > 1) {
            names += ' and '
          } else if (instructorNameIndex > 0) {
            names += ', '
          }
          names += ((instructorName.indexOf('__') === -1) ? instructorName : instructorName.substring(0, instructorName.indexOf('__')))
        }
        return names
      },
      getFormattedInstructorName (course) {
        let instructors = course.instructorNames
        if (instructors && instructors.length > 0) {
          return this.formatInstructorNames(instructors)
        }
        return ''
      },
      removeButtonClicked (course) {
        const params = {
          streamUuid: this.streamUuid,
          currentYear: this.currentYear,
          currentSemester: this.currentSemester,
          courseData: course.courseData,
          userRole: this.userRole
        }
        this.removeCourseFromStream(params).then((response) => {
          // NOTE: The backend updates the course's hasConflict status, so we make another call to reflect this here
          // in the UI's color
          this.loadData()
        })
        this.notifySuccess('Successfully removed \'' + course.courseName + '\' from your stream.')
      },
      courseHasConflict (course) {
        let hasConflict = false
        if (course.meetingDaysFormatted) {
          course.meetingDaysFormatted.forEach(function (meetingDay) {
            if (meetingDay.conflict) {
              hasConflict = true
            }
          })
        }
        return hasConflict
      },
      getFormattedDayAndTimeStrings (course) {
        let days = ''
        let times = ''

        if (course.meetingDaysRaw) {
          course.meetingDaysRaw.forEach(function (meetingDay) {
            days += (meetingDay.day) ? (meetingDay.day + '; ') : ''
            times += (meetingDay.meetingFromTime) ? (meetingDay.meetingFromTime + ' - ' + meetingDay.meetingToTime + '; ') : ''
          })
        }
        return {days: days, times: times}
      },
      getCoursePlanningPackage () {
        let packages = this.packages
        if (!packages || !packages.length || packages.length <= 0) {
          return {}
        }
        return PackageUtil.getCoursePlanningPackage(packages)
      }
    },
    computed: {
      ...mapState({
        'courses': state => state.semester.courses,
        'iCalFile': state => state.semester.iCalFile,
        'packages': state => state.institution.packages
      }),
      formattedCourses () {
        // compile the meetingDaysRaw objects into a 'days' and 'times' string for each course
        // join each Instructor name with commas and a penultimate "and"
        let formattedCourses = []
        let courses = this.courses

        let formatInstructorName = this.getFormattedInstructorName
        let courseHasConflict = this.courseHasConflict
        let getFormattedDayAndTime = this.getFormattedDayAndTimeStrings

        courses.forEach(function (obj) {
          let hasConflict = courseHasConflict(obj)
          let formattedDayAndTime = getFormattedDayAndTime(obj)

          let formattedCourse = {
            CRN: obj.crn,
            courseName: obj.title,
            days: formattedDayAndTime['days'],
            time: formattedDayAndTime['times'],
            numberOfUnits: obj.creditHour,
            professorOrInstructor: formatInstructorName(obj),
            block: (obj.block && obj.block.length && obj.block.length > 0) ? obj.block.join(', ') : '',
            gradeLevel: (obj.gradeLevel && obj.gradeLevel.length && obj.gradeLevel.length > 0) ? obj.gradeLevel.join(', ') : '',
            _rowVariant: (hasConflict) ? 'danger' : 'success',
            courseData: obj
          }
          formattedCourses.push(formattedCourse)
        })
        return formattedCourses
      },
      iCalEvents () {
        let iCalFile = this.iCalFile

        return iCalFile['fileContents']
      },
      scheduleCheckFlagMap () {
        let featureId = PACKAGE.FEATURE_IDS.SCHEDULE_CHECK_FEATURE_ID
        let coursePlanningPackage = this.getCoursePlanningPackage()
        return PackageUtil.getFlagsFromPackageFeature(coursePlanningPackage, featureId)
      },
      formattedFields () {
        let fields = this.fields
        // 'remove column'
        fields.pop()
        let flagMap = this.scheduleCheckFlagMap
        if (flagMap && flagMap.showCRN && flagMap.showCRN.enabled) {
          fields.push('CRN')
        }
        if (flagMap && flagMap.showCourseTitle && flagMap.showCourseTitle.enabled) {
          fields.push('courseName')
        }
        if (flagMap && flagMap.showDays && flagMap.showDays.enabled) {
          fields.push('days')
        }
        if (flagMap && flagMap.showTime && flagMap.showTime.enabled) {
          fields.push('time')
        }
        if (flagMap && flagMap.showUnits && flagMap.showUnits.enabled) {
          fields.push('numberOfUnits')
        }
        if (flagMap && flagMap.showInstructor && flagMap.showInstructor.enabled) {
          fields.push('professorOrInstructor')
        }
        if (flagMap && flagMap.showBlock && flagMap.showBlock.enabled) {
          fields.push('block')
        }
        if (flagMap && flagMap.showGradeLevel && flagMap.showGradeLevel.enabled) {
          fields.push('gradeLevel')
        }
        fields.push('remove')
        return fields
      }
    }
  }
</script>

<style scoped>
.conflict {
    color: red
}

.no-conflict {
    color: green
}
</style>



// WEBPACK FOOTER //
// src/app/semester/components/SemesterDetailView.vue